/**
 * @fileoverview Component that renders a page layout without a sidebar, with optional fixed components at the top
 * and a scrollable content area below.
 * @module PageWithoutSidebar
 */

import * as React from 'react';
import { Fragment, FunctionComponent } from 'react';

import { Flex } from 'basecamp';

/**
 * Props for the PageWithoutSidebar component
 * @typedef {Object} Props
 * @property {string} [dataTest] - Data test attribute for testing
 * @property {React.ReactNode[]} [headerElements] - Array of components to render at the top of the page (navigation, filters, titles)
 * @property {boolean} [preventScroll] - Whether to prevent scrolling in the content area
 */
type Props = {
	dataTest?: string;
	headerElements: React.ReactNode[];
	preventScroll?: boolean;
	offsetHeight?: number;
};

const PageWithoutSidebar: FunctionComponent<Props> = ({
	dataTest,
	headerElements = [],
	offsetHeight = 154,
	preventScroll,
	children,
}) => {
	return (
		<Flex
			setAs="column"
			dataTest={`${dataTest} page-without-sidebar`}
			minHeight={`calc(100vh - ${offsetHeight}px)`}
			overflow="hidden"
		>
			<Flex id="pageHeaderElements" setAs="column" flexShrink={0} p={0} m={0} width={1}>
				{headerElements.map((component, index) => (
					<Fragment key={index}>{component}</Fragment>
				))}
			</Flex>
			<Flex
				id="content"
				setAs="column"
				dataTest={dataTest}
				flexGrow={1}
				overflowY={preventScroll ? 'hidden' : 'auto'}
				zIndex={9}
				position="relative"
				px={4}
				py={0}
				m={0}
			>
				{children}
			</Flex>
		</Flex>
	);
};

export default PageWithoutSidebar;
